/**
 * WebRTC Peer Connection Manager for Captain Mesh Transport
 * 
 * Uses native RTCPeerConnection and RTCDataChannel to establish
 * direct peer-to-peer event distribution.
 */

import { getOrGenerateDeviceIdentity, getActiveActorSession, signChallenge } from "./identity";
import { createChallenge, authenticatePeerConnection } from "./auth";
import { handleDataChannelMessage } from "./ingestion";

const RTC_CONFIG = {
  // Local restaurant LANs generally don't need STUN/TURN, but we include Google's public STUN
  // purely to aid in NAT traversal if the local network isolates clients (AP isolation).
  iceServers: [{ urls: "stun:stun.l.google.com:19302" }]
};

const SIGNALING_RETRY_MIN_MS = 1000;
const SIGNALING_RETRY_MAX_MS = 10000;
const SIGNALING_REPLACED = 4000;

/** The hub's address on the page's own server: wss:// beside https://. */
export function signalingUrlFor(base, deviceId, restaurantId, token) {
  const url = new URL(base || window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/api/ws/signaling";
  url.search = "";
  url.searchParams.append("device_id", deviceId);
  url.searchParams.append("restaurant_id", restaurantId);
  if (token) url.searchParams.append("token", token);
  return url.toString();
}

class PeerManager {
  constructor() {
    this.peers = new Map(); // device_id -> RTCPeerConnection
    this.channels = new Map(); // device_id -> RTCDataChannel
    // Everyone we have ever exchanged signaling with. PEER_JOINED only fires
    // for NEW arrivals, so after a dropped link there is no event to rebuild
    // from — reconnection has to come from memory.
    this.knownPeers = new Set();
    // Test hook: a real radio partition drops packets, it does not run cleanup
    // code. While suspended we ignore signaling and initiate nothing, so the
    // partition the e2e simulates cannot be healed early by our own retries.
    this.suspended = false;
    this.signalingSocket = null;
    this.identity = null;
    this.restaurantId = "DEFAULT_RESTAURANT"; // From bootstrap configuration
    
    // Callbacks for the Coordinator and Sync engine
    this.onPeerConnected = null;
    this.onPeerDisconnected = null;
  }

  async initialize(signalingUrl, deviceId, restaurantId, token) {
    if (this.initialized) return;
    this.signalingUrl = signalingUrl;
    this.deviceId = deviceId;
    this.restaurantId = restaurantId;
    this.token = token;
    this.initialized = true;

    try {
      this.identity = await this.resolveIdentity(deviceId, token);
    } catch (err) {
      console.error("[PeerManager] Failed to initialize device identity", err);
    }

    this.start();
  }

  /**
   * The credential this tablet presents to peers.
   *
   * It is the ACTOR's, not a second device-only keypair. The server keeps one
   * active credential per (actor_id, device_id), so enrolling a device key
   * beside the actor key supersedes whichever was issued first and every signed
   * event is then refused. Advertising `authorization` while signing with a
   * different private key fails peer verification the same way. One keypair,
   * issued at login, signs both mesh events and peer challenges.
   */
  async resolveIdentity(deviceId, token) {
    const session = getActiveActorSession();
    if (session && session.authorization && session.privateKey) {
      return {
        device_id: session.device_id || deviceId,
        privateKey: session.privateKey,
        publicKeyJwk: session.authorization.public_key_jwk,
        authorization: session.authorization,
      };
    }
    // No live actor session (a reload drops it — the key is PIN-wrapped and the
    // PIN is not stored). Keep the device record so device_id stays stable, but
    // do not claim mesh authorization we cannot back with a signature.
    const device = await getOrGenerateDeviceIdentity(deviceId, token);
    return { ...device, authorization: null };
  }

  async start() {
    if (!this.identity || !this.identity.authorization) {
      // Re-read: login may have finished enrolling since initialize() ran.
      this.identity = await this.resolveIdentity(this.deviceId, this.token);

      if (!this.identity || !this.identity.authorization) {
        console.warn("[PeerManager] No actor credential yet — peer mesh idle. Retrying in 1s...");
        setTimeout(() => this.start(), 1000);
        return;
      }
    }

    if (this.signalingSocket) return;

    // The session's current token: a reconnect hours after login must not
    // present the one captured at mount, which may have been replaced since.
    let token = this.token;
    try {
      token = JSON.parse(localStorage.getItem("vibez-session") || "{}").token || token;
    } catch { /* keep the mount-time token */ }
    const socket = new WebSocket(signalingUrlFor(this.signalingUrl, this.identity.device_id, this.restaurantId, token));
    this.signalingSocket = socket;

    socket.onopen = () => {
      this._signalingRetryMs = SIGNALING_RETRY_MIN_MS;
      // Nothing to initiate here: the hub announces this pad as PEER_JOINED and
      // the others offer to it. Offering from both ends at once crossed the
      // offers and left the link stuck half-made.
    };

    socket.onmessage = async (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      try {
        await this.handleSignalingMessage(msg);
      } catch (err) {
        console.warn("[PeerManager] signaling message failed", msg?.type, err);
      }
    };

    // A dropped signaling socket used to stay dropped: nothing reopened it, so
    // one Wi-Fi blip took a pad off the mesh until the app restarted. Reopen on
    // our own, backing off to a ceiling so a server that is down is not hammered.
    socket.onclose = (event) => {
      if (this.signalingSocket !== socket) return;
      this.signalingSocket = null;
      // 4000: the hub handed this device's slot to a newer connection of the
      // same person's (a second copy of the app). Reconnecting would push that
      // one off in turn, and the two would swap every second.
      if (event?.code === SIGNALING_REPLACED) return;
      const wait = this._signalingRetryMs || SIGNALING_RETRY_MIN_MS;
      this._signalingRetryMs = Math.min(wait * 2, SIGNALING_RETRY_MAX_MS);
      clearTimeout(this._signalingRetry);
      this._signalingRetry = setTimeout(() => this.start(), wait);
    };

    // Keepalive ping for the signaling websocket (one timer, however often the
    // socket is reopened).
    if (!this._keepalive) {
      this._keepalive = setInterval(() => {
        if (this.signalingSocket?.readyState === WebSocket.OPEN) {
          this.signalingSocket.send(JSON.stringify({ type: "ping" }));
        }
      }, 10000);
    }
  }

  async handleSignalingMessage(msg) {
    const { type, source, device_id } = msg;
    console.log(`[WebRTC] handleSignalingMessage: ${type} from ${source || device_id || 'server'}`);

    if (source) this.knownPeers.add(source);
    if (device_id) this.knownPeers.add(device_id);
    if (this.suspended) return;

    if (type === "PEER_JOINED") {
      // The newly joined peer doesn't know about us, so existing peers MUST initiate.
      await this.createConnection(device_id, true);
    } else if (type === "PEER_LEFT") {
      this.closeConnection(device_id);
    } else if (type === "SDP_OFFER") {
      await this.handleOffer(source, msg.offer);
    } else if (type === "SDP_ANSWER") {
      await this.handleAnswer(source, msg.answer);
    } else if (type === "ICE_CANDIDATE") {
      await this.handleIceCandidate(source, msg.candidate);
    }
  }

  sendSignaling(target, message) {
    if (this.signalingSocket?.readyState === WebSocket.OPEN) {
      this.signalingSocket.send(JSON.stringify({ target, ...message }));
    }
  }

  /** Re-initiate to a known peer iff disconnected, we hold the smaller id
   * (glare avoidance), signaling is up, and no partition is being simulated. */
  async ensurePeer(targetDeviceId) {
    if (this.suspended) return;
    if (this.peers.has(targetDeviceId)) return;
    if (this.signalingSocket?.readyState !== WebSocket.OPEN) return;
    const myId = this.identity?.device_id;
    if (!myId || myId > targetDeviceId) return;
    await this.createConnection(targetDeviceId, true);
  }

  /** Deliberate reconnect (devtools / recovery): initiate to every known peer
   * we are not currently connected to, ordering rule waived — an explicit
   * reconnect outranks glare avoidance and the other side's peers.has() guard
   * absorbs a crossed attempt. */
  async reconnectKnownPeers() {
    this.suspended = false;
    for (const id of this.knownPeers) {
      if (!this.peers.has(id)) {
        await this.createConnection(id, true);
      }
    }
  }

  async createConnection(targetDeviceId, isInitiator) {
    if (this.peers.has(targetDeviceId)) return;

    const pc = new RTCPeerConnection(RTC_CONFIG);
    this.peers.set(targetDeviceId, pc);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignaling(targetDeviceId, {
          type: "ICE_CANDIDATE",
          candidate: event.candidate
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        this.closeConnection(targetDeviceId);
        // A dropped link fires no PEER_JOINED — both sides are still "joined"
        // on the hub — so without this retry a mid-service WebRTC drop stayed
        // dropped until someone reloaded the app. Only the smaller device id
        // initiates, so both sides retrying cannot glare into crossed offers.
        setTimeout(() => this.ensurePeer(targetDeviceId), 2000);
      }
    };

    if (isInitiator) {
      const channel = pc.createDataChannel("mesh-events", {
        ordered: true, // TCP-like reliability
      });
      this.setupDataChannel(targetDeviceId, channel);

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      this.sendSignaling(targetDeviceId, { type: "SDP_OFFER", offer });
    } else {
      pc.ondatachannel = (event) => {
        this.setupDataChannel(targetDeviceId, event.channel);
      };
    }
  }

  async handleOffer(source, offer) {
    await this.createConnection(source, false);
    const pc = this.peers.get(source);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    this.sendSignaling(source, { type: "SDP_ANSWER", answer });
  }

  async handleAnswer(source, answer) {
    const pc = this.peers.get(source);
    if (pc) {
      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    }
  }

  async handleIceCandidate(source, candidate) {
    const pc = this.peers.get(source);
    if (pc) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    }
  }

  setupDataChannel(peerId, channel) {
    console.log(`[WebRTC] Setting up data channel for peer ${peerId}. ReadyState: ${channel.readyState}`);
    channel.binaryType = "arraybuffer";

    const initiateHandshake = () => {
      console.log(`[WebRTC] Initiating handshake HELLO to peer ${peerId}`);
      // Initiate handshake challenge
      const challenge = createChallenge();
      channel.send(JSON.stringify({
        type: "HELLO",
        device_id: this.identity.device_id,
        challenge: challenge,
        authorization: this.identity.authorization
      }));

      // Store the challenge we expect them to sign
      channel._expectedChallenge = challenge;
      channel._isAuthenticated = false;
    };

    if (channel.readyState === 'open') {
      initiateHandshake();
    } else {
      channel.onopen = initiateHandshake;
    }

    channel.onclose = () => this.closeConnection(peerId);

    channel.onmessage = async (event) => {
      const message = JSON.parse(event.data);
      console.log(`[WebRTC] Received message type ${message.type} from ${peerId}`);

      if (message.type === "HELLO") {
        console.log(`[WebRTC] Signing challenge for peer ${peerId}`);
        // Peer is challenging us. Sign their challenge and send back PEER_INFO response.
        const signature = await signChallenge(this.identity.privateKey, message.challenge);
        
        console.log(`[WebRTC] Sending PEER_INFO to ${peerId}`);
        channel.send(JSON.stringify({
          type: "PEER_INFO",
          device_id: this.identity.device_id,
          authorization: this.identity.authorization,
          signature: signature,
        }));

        // We also verify their authorization record (but not their signature yet, 
        // as they haven't responded to our challenge)
        if (message.authorization?.restaurant_id !== this.restaurantId) {
          console.warn(`[PeerManager] Rejecting ${peerId}: Invalid restaurant_id in HELLO`);
          channel.close();
        }
        return;
      }

      if (message.type === "PEER_INFO") {
        console.log(`[WebRTC] Authenticating PEER_INFO from ${peerId}`);
        // Peer has responded to our challenge.
        const isValid = await authenticatePeerConnection(
          this.restaurantId,
          channel._expectedChallenge,
          message.authorization,
          message.signature
        );

        if (isValid) {
          console.log(`[WebRTC] Peer ${peerId} is fully authenticated!`);
          channel._isAuthenticated = true;
          this.channels.set(peerId, channel);
          if (this.onPeerConnected) this.onPeerConnected(peerId);
        } else {
          console.warn(`[WebRTC] Authentication failed for ${peerId}`);
          channel.close();
        }
        return;
      }

      // If not authenticated, drop all other messages
      if (!channel._isAuthenticated) return;

      // Authenticated Event Pipeline
      await handleDataChannelMessage(peerId, message, this);
    };
  }

  closeConnection(peerId) {
    const channel = this.channels.get(peerId);
    if (channel) channel.close();
    this.channels.delete(peerId);

    const pc = this.peers.get(peerId);
    if (pc) pc.close();
    this.peers.delete(peerId);

    if (this.onPeerDisconnected) this.onPeerDisconnected(peerId);
  }

  broadcast(messageObj) {
    const data = JSON.stringify(messageObj);
    for (const channel of this.channels.values()) {
      if (channel.readyState === "open" && channel._isAuthenticated) {
        channel.send(data);
      }
    }
  }

  sendToPeer(peerId, messageObj) {
    const channel = this.channels.get(peerId);
    if (channel && channel.readyState === "open" && channel._isAuthenticated) {
      channel.send(JSON.stringify(messageObj));
    }
  }

  getConnectedPeers() {
    return Array.from(this.channels.keys());
  }
}

export const peerManager = new PeerManager();
